import React, { useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import { useNotification } from "@context/NotificationContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { useIncrementMutations } from "../hooks/useIncrementMutations";
import { useGatedOpen } from "../hooks/usePollingGate";
import { problemMessage } from "../utils/problemDetail";
import type { ActiveExecution } from "./executionContext";
import { completeSchema, startSchema } from "./incrementSchemas";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.sequences.execution.increment`;

type StartFormData = z.infer<ReturnType<typeof startSchema>>;
type CompleteFormData = z.infer<ReturnType<typeof completeSchema>>;

const StartDialog: React.FC<{
  operationId: string;
  execution: ActiveExecution;
  onClose: () => void;
}> = ({ operationId, execution, onClose }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { start } = useIncrementMutations(operationId);
  const schema = useMemo(() => startSchema(t), [t]);
  const form = useForm<StartFormData>({
    resolver: zodResolver(schema),
    defaultValues: { ats: null },
  });
  const isFirst = execution.kind === "start";

  const submit = form.handleSubmit(async (values) => {
    try {
      await start.mutateAsync({
        sequenceId: execution.sequence.id,
        request: { ats: values.ats ? values.ats.toISOString() : null },
      });
      onClose();
    } catch (error) {
      notify({
        type: "error",
        title: t("common.error"),
        description: problemMessage(error) ?? t(`${L}.failed`),
      });
    }
  });

  return (
    <GenericDialog
      open
      testId="increment-dialog"
      title={t(`${L}.${isFirst ? "title-start" : "title-add"}`, {
        number: execution.sequence.sequenceNo,
      })}
      cancelButtonText={t(`${L}.cancel`)}
      confirmButtonText={t(`${L}.${isFirst ? "confirm-start" : "confirm-add"}`)}
      handleCloseDialog={onClose}
      confirmAction={() => void submit()}
      isSubmitting={start.isPending}
      elementContent={
        <div className="flex flex-col gap-3">
          <FormProvider {...form}>
            <FormItem<StartFormData, "ats">
              name="ats"
              type="date-picker"
              timePicker
              combinedInput
              dateConstraint="not-future"
              label={t(`${L}.fields.ats`)}
              testId="increment-ats"
            />
          </FormProvider>
          <span className="text-sm text-gray-600">{t(`${L}.ats-hint`)}</span>
        </div>
      }
    />
  );
};

const CompleteDialog: React.FC<{
  operationId: string;
  execution: ActiveExecution;
  onClose: () => void;
}> = ({ operationId, execution, onClose }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { complete } = useIncrementMutations(operationId);
  const increment = execution.increment;
  const schema = useMemo(() => completeSchema(t, increment?.ats), [t, increment?.ats]);
  const form = useForm<CompleteFormData>({
    resolver: zodResolver(schema),
    defaultValues: { quantity: "", atc: undefined, ats: null, final: false },
  });

  const submit = form.handleSubmit(async (values) => {
    if (!increment) return;
    try {
      await complete.mutateAsync({
        sequenceId: execution.sequence.id,
        incrementId: increment.id,
        request: {
          quantity: Number(values.quantity),
          atc: values.atc.toISOString(),
          ats: values.ats ? values.ats.toISOString() : null,
          final: values.final,
        },
      });
      onClose();
    } catch (error) {
      notify({
        type: "error",
        title: t("common.error"),
        description: problemMessage(error) ?? t(`${L}.failed`),
      });
    }
  });

  return (
    <GenericDialog
      open
      testId="increment-dialog"
      title={t(`${L}.title-complete`, { number: execution.sequence.sequenceNo })}
      cancelButtonText={t(`${L}.cancel`)}
      confirmButtonText={t(`${L}.confirm-complete`)}
      handleCloseDialog={onClose}
      confirmAction={() => void submit()}
      isSubmitting={complete.isPending}
      elementContent={
        <div className="flex flex-col gap-3">
          <FormProvider {...form}>
            <FormItem<CompleteFormData, "quantity">
              required
              name="quantity"
              type="text"
              label={t(`${L}.fields.quantity`)}
              testId="increment-quantity"
              slotProps={{ htmlInput: { inputMode: "decimal" } }}
            />
            <FormItem<CompleteFormData, "atc">
              required
              name="atc"
              type="date-picker"
              timePicker
              combinedInput
              dateConstraint="not-future"
              label={t(`${L}.fields.atc`)}
              testId="increment-atc"
            />
            <FormItem<CompleteFormData, "ats">
              name="ats"
              type="date-picker"
              timePicker
              combinedInput
              dateConstraint="not-future"
              label={t(`${L}.fields.ats-correction`)}
              testId="increment-ats-correction"
            />
            <FormItem<CompleteFormData, "final">
              name="final"
              type="checkbox"
              label={t(`${L}.fields.final`)}
              testId="increment-final"
            />
          </FormProvider>
        </div>
      }
    />
  );
};

/** A form: it holds the polling gate while open. */
const IncrementDialog: React.FC<{
  operationId: string;
  execution: ActiveExecution;
  onClose: () => void;
}> = ({ operationId, execution, onClose }) => {
  useGatedOpen(true);
  return execution.kind === "complete" ? (
    <CompleteDialog operationId={operationId} execution={execution} onClose={onClose} />
  ) : (
    <StartDialog operationId={operationId} execution={execution} onClose={onClose} />
  );
};

export default IncrementDialog;
